import { useEffect, useState } from "react";
import { useCreateWorkerEvent } from "../hooks/useTimeEvents";
import { useUiStore } from "../state/uiStore";
import { Sheet } from "../components/Sheet";
import { ActivityLinkFields } from "../components/ActivityLinkFields";
import {
  buildContextRefs,
  EMPTY_LINKS,
  type ActivityLinks,
} from "../lib/activityLinks";
import {
  DAY_START_MIN,
  DAY_END_MIN,
  STEP_MIN,
  minToLabel,
  slotIso,
} from "../lib/timelineSlots";
import type { ActivityCatalogEntry } from "../api/types";

export interface SlotDraft {
  startMin: number;
  endMin: number;
}

interface Props {
  draft: SlotDraft | null;
  day: Date;
  catalog: ActivityCatalogEntry[];
  /** Fin max autorisée (minute du jour, cf. slotMaxEndMin) ; null = libre.
   * Bloque les créneaux futurs ou chevauchant l'activité en cours, dont
   * l'activity_end effacerait l'état live dérivé (deriveSessionState). */
  maxEndMin: number | null;
  onClose: () => void;
}

const TIME_OPTIONS: number[] = [];
for (let m = DAY_START_MIN; m <= DAY_END_MIN; m += STEP_MIN)
  TIME_OPTIONS.push(m);

/** Modal « Créneau » du design : activité (catalogue) + début/fin (15 min) + note.
 * Persisté via POST /workers/timeevents (self-service, semaine draft uniquement) :
 * un activity_start + un activity_end passés. Le code voyage dans context_refs
 * (la colonne activity_code n'est pas exposée sur ce chemin d'API). */
export function SlotEditorSheet({
  draft,
  day,
  catalog,
  maxEndMin,
  onClose,
}: Props) {
  const pushToast = useUiStore((s) => s.pushToast);
  const createEvent = useCreateWorkerEvent();

  const [activityCode, setActivityCode] = useState("");
  const [startMin, setStartMin] = useState(DAY_START_MIN);
  const [endMin, setEndMin] = useState(DAY_START_MIN + 60);
  const [note, setNote] = useState("");
  const [links, setLinks] = useState<ActivityLinks>(EMPTY_LINKS);

  // Réinitialisation au changement de draft (nouveau créneau) uniquement.
  // `catalog` est volontairement hors deps : un refetch React Query (staleTime
  // 5 min / retour focus) renvoie une nouvelle référence et effacerait sinon la
  // saisie en cours. Le défaut d'activité est complété par l'effet suivant.
  // `endMin` est borné à la limite (drag pouvant dépasser une activité live).
  useEffect(() => {
    if (!draft) return;
    setStartMin(draft.startMin);
    setEndMin(Math.min(draft.endMin, maxEndMin ?? DAY_END_MIN));
    setActivityCode(catalog[0]?.code ?? "");
    setNote("");
    setLinks(EMPTY_LINKS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Catalogue chargé après l'ouverture : pose le défaut sans écraser la saisie.
  useEffect(() => {
    const first = catalog[0];
    if (draft && !activityCode && first) setActivityCode(first.code);
  }, [draft, activityCode, catalog]);

  if (!draft) return null;

  const limit = maxEndMin ?? DAY_END_MIN;
  const noSlotPossible = limit < DAY_START_MIN + STEP_MIN;
  const overLimit = endMin > limit;
  const valid = activityCode && endMin >= startMin + STEP_MIN && !overLimit;

  async function handleSave() {
    if (!valid) return;
    const refs: Record<string, unknown> = {
      activity_code: activityCode,
      ...(note.trim() ? { note: note.trim() } : {}),
      ...buildContextRefs(links),
    };
    const reason = "Créneau saisi via la timeline";
    // ponytail: 2 writes séquentiels sans transaction backend : on suit la phase
    // pour ne pas rejouer le start (doublon) en cas d'échec du end, et pour
    // afficher un message exact au lieu du refus métier générique.
    let startDone = false;
    try {
      await createEvent.mutateAsync({
        eventType: "activity_start",
        occurredAt: slotIso(day, startMin),
        reason,
        contextRefs: refs,
      });
      startDone = true;
      await createEvent.mutateAsync({
        eventType: "activity_end",
        occurredAt: slotIso(day, endMin),
        reason,
        contextRefs: { activity_code: activityCode },
      });
      pushToast({
        variant: "info",
        title: "Créneau enregistré",
        subtitle: `${minToLabel(startMin)}-${minToLabel(endMin)}`,
      });
      onClose();
    } catch {
      if (startDone) {
        // Start persisté, end échoué : le début orphelin apparaît « en cours »
        // et se clôt via le flux de correction. On ferme pour éviter qu'un
        // retour sur ce draft ne recrée un second start (doublon).
        pushToast({
          variant: "error",
          title: "Créneau incomplet",
          subtitle: `Début ${minToLabel(startMin)} enregistré, fin non enregistrée : corrigez le créneau en cours.`,
        });
        onClose();
      } else {
        pushToast({
          variant: "error",
          title: "Erreur",
          subtitle:
            "Créneau refusé : seule la semaine en cours (feuille en brouillon) est modifiable.",
        });
      }
    }
  }

  return (
    <Sheet
      title="Créneau"
      onClose={onClose}
      footer={
        <div className="sheet-cta">
          <button
            className="primary"
            disabled={!valid || createEvent.isPending}
            onClick={() => void handleSave()}
          >
            Enregistrer le créneau
          </button>
        </div>
      }
    >
      <div className="link-fields">
        <label className="link-field">
          <span className="k">Activité</span>
          <select
            className="field"
            value={activityCode}
            onChange={(e) => setActivityCode(e.target.value)}
          >
            {catalog.map((a) => (
              <option key={a.code} value={a.code}>
                {a.code} · {a.label}
              </option>
            ))}
          </select>
        </label>
        <div style={{ display: "flex", gap: 10 }}>
          <label className="link-field" style={{ flex: 1 }}>
            <span className="k">Début</span>
            <select
              className="field"
              value={startMin}
              onChange={(e) => setStartMin(Number(e.target.value))}
            >
              {TIME_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {minToLabel(m)}
                </option>
              ))}
            </select>
          </label>
          <label className="link-field" style={{ flex: 1 }}>
            <span className="k">Fin</span>
            <select
              className="field"
              value={endMin}
              onChange={(e) => setEndMin(Number(e.target.value))}
            >
              {TIME_OPTIONS.filter(
                (m) => m >= startMin + STEP_MIN && m <= limit,
              ).map((m) => (
                <option key={m} value={m}>
                  {minToLabel(m)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="link-field">
          <span className="k">Note</span>
          <input
            className="field"
            placeholder="Note (optionnel)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
          />
        </label>
        <ActivityLinkFields value={links} onChange={setLinks} />
        {(overLimit || noSlotPossible) && (
          <div className="form-error">
            {noSlotPossible
              ? "Aucun créneau possible sur ce jour : activité en cours ou jour futur."
              : `Fin au plus tard ${minToLabel(limit)} : pas de créneau dans le futur ni chevauchant l'activité en cours.`}
          </div>
        )}
      </div>
    </Sheet>
  );
}
